import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import AccordionArrow from "../icons/AccordionArrow";
import Avatar from "../avatar/Avatar";
import CoinBadge from "../coin_badge/CoinBadge";
import users from '../../assets/icon/users.png'
import cup from '../../assets/icon/cup.png'
import tokenApi from "../../api/tokenApi";
import CrystalsApi from "../../api/CrystalsApi";
import blockchainApi from "../../api/blockchainApi";
import { getCrystalRarityColorMap, getCrystalRarityTier } from "../../data/crystalRarityTiers";
import { getPromoterDisplayName, getPromoterAvatarSrc } from "../../store/slices/promoTelegramSlice";
import getIcon from "../../utils/Icon";
import Lottie from 'lottie-react';
import swipeAnim from '../../assets/lottie/swipe.json';

const IMAGE_BASE_URL = 'https://api.duotetra.quest/uploads/gems/';

/** Resolve per-winner crypto drop; rebuild from % when Math.round saved zeros. */
function resolvePositionDropPerWinner(position, promo) {
    const winners = Number(position?.winners) || 0;
    const storedDrop = Number(position?.dropPerWinner);
    if (Number.isFinite(storedDrop) && storedDrop > 0) return storedDrop;

    const totalAmount = Number(promo?.totalAmount) || 0;
    let budgetAmount = Number(position?.budget) || 0;

    if (!(budgetAmount > 0) && totalAmount > 0) {
        const positions = promo?.category?.positions || [];
        const pct = Number(position?.budgetPercent) || 0;
        const totalPct = positions.reduce((sum, p) => sum + (Number(p?.budgetPercent) || 0), 0);
        if (pct > 0 && totalPct > 0) {
            budgetAmount = (pct / totalPct) * totalAmount;
        }
    }

    if (!(budgetAmount > 0)) return Number.isFinite(storedDrop) ? storedDrop : 0;
    return winners > 0 ? budgetAmount / winners : budgetAmount;
}

/** Same as Crypto & Crystals form modal: show amount as-is in CoinBadge. */
function formatRewardModalCoinBadgeCount(amount) {
    if (amount == null) return 0;
    if (typeof amount !== 'number' || Number.isNaN(amount)) return String(amount);

    if (amount > 0 && amount < 1) {
        if (Number.isInteger(amount)) return String(amount);
        return amount.toFixed(8);
    }

    const abs = Math.abs(amount);
    let decimals;
    if (abs >= 10_000) decimals = 0;
    else if (abs >= 1_000) decimals = 1;
    else if (abs >= 100) decimals = 2;
    else if (abs >= 10) decimals = 3;
    else if (abs >= 1) decimals = 4;
    else return String(amount);

    const formatted = decimals === 0
        ? String(Math.round(abs))
        : abs.toFixed(decimals).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');

    const withSign = amount < 0 ? `-${formatted}` : formatted;
    if (abs < 1000) return withSign;

    const isNegative = withSign.startsWith('-');
    const unsigned = isNegative ? withSign.slice(1) : withSign;
    const [intPart, decPart] = unsigned.split('.');
    const spacedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const spaced = decPart !== undefined ? `${spacedInt}.${decPart}` : spacedInt;
    return isNegative ? `-${spaced}` : spaced;
}

const slideVariants = {
    enter: (dir) => ({
        x: dir === 0 ? 0 : dir > 0 ? 80 : -80,
        opacity: dir === 0 ? 1 : 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({
        x: dir > 0 ? -80 : 80,
        opacity: 0,
    }),
};

function PrizePoolModal({ onClose, promo, gems, token, isOnlyCrystals, isBonusPromo, initialSlideIndex = 0 }) {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [slideIndex, setSlideIndex] = useState(initialSlideIndex);
    const [direction, setDirection] = useState(0);
    const [showSwipeHint, setShowSwipeHint] = useState(false);

    const positions = promo?.category?.positions || [];
    const isCryptoOnly = promo?.promoType === 'cryptoOnly';
    const positionsCount = positions.length;

    const gemsMap = {};
    gems.forEach((gem) => {
        if (gem.position) gemsMap[gem.position] = gem;
    });

    const positionIcons = Array.from({ length: positionsCount }, (_, index) => {
        const reversedPosition = positionsCount - index;
        const gem = gemsMap[reversedPosition];
        return gem?.frontImage ? `${IMAGE_BASE_URL}${gem.frontImage}` : '';
    });

    const cryptoColors = getCrystalRarityColorMap(positionsCount);

    useEffect(() => {
        setSlideIndex(initialSlideIndex);
        setDirection(0);
    }, [initialSlideIndex]);

    useEffect(() => {
        setShowSwipeHint(positionsCount > 1);
        if (positionsCount <= 1) return undefined;
        const timer = setTimeout(() => setShowSwipeHint(false), 2500);
        return () => clearTimeout(timer);
    }, [positionsCount]);

    useEffect(() => {
        setImageLoaded(false);
        setImageError(false);
    }, [slideIndex]);

    if (positionsCount === 0) return null;

    const position = positions[slideIndex];
    const rewardTier = getCrystalRarityTier(slideIndex, positionsCount);
    const rewardAmount = promo?.promoType === 'crystalsAndCrypto'
        ? resolvePositionDropPerWinner(position, promo)
        : (position?.dropPerWinner || position?.budget || 0);
    const winPercent = position?.winPercent ?? position?.win ?? 0;
    const tokenSymbol = token?.symbol || token?.name || 'ETH';
    const blockchainKey = promo?.selectedBlockchain || 'ETH';
    const tokenLogo = token?.logoPath ? `https://api.duotetra.quest/${token.logoPath}` : undefined;
    const crystalImage = (() => {
        if (isCryptoOnly) return null;
        const gemPosition = positionsCount - slideIndex;
        const gem = gemsMap[gemPosition];
        return gem?.frontImage ? `${IMAGE_BASE_URL}${gem.frontImage}` : (positionIcons[slideIndex] || null);
    })();
    const cryptoColor = cryptoColors[slideIndex] || cryptoColors[cryptoColors.length - 1];

    const goToSlide = (nextIndex) => {
        if (positionsCount <= 1) return;

        let target = nextIndex;
        if (target < 0) target = positionsCount - 1;
        if (target >= positionsCount) target = 0;
        if (target === slideIndex) return;

        setShowSwipeHint(false);
        let dir;
        if (slideIndex === positionsCount - 1 && target === 0) {
            dir = 1;
        } else if (slideIndex === 0 && target === positionsCount - 1) {
            dir = -1;
        } else {
            dir = target > slideIndex ? 1 : -1;
        }
        // Порядок N…1 — инвертируем анимацию под свайп влево
        dir = -dir;
        setDirection(dir);
        setSlideIndex(target);
    };

    const goNext = () => goToSlide(slideIndex - 1);
    const goPrev = () => goToSlide(slideIndex + 1);

    return (
        <motion.div
            className="fixed inset-0 z-50 flex justify-center px-4 pb-4 pt-[70%]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
        >
            <motion.div
                className="absolute inset-0 bg-[#2C2C2CE5]/90 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            />
            <motion.div
                className="relative z-10 w-full max-w-[300px] h-[300px] px-3 overflow-hidden"
                initial={{ opacity: 0, scale: 0.92, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: 16 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                onClick={(e) => e.stopPropagation()}
            >
                <AnimatePresence initial={false} custom={direction} mode="wait">
                    <motion.div
                        key={slideIndex}
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        drag="x"
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={0.15}
                        onDragEnd={(_, info) => {
                            if (info.offset.x < -30 || info.velocity.x < -30) {
                                goNext();
                            } else if (info.offset.x > 30 || info.velocity.x > 30) {
                                goPrev();
                            }
                        }}
                        className="relative flex flex-col items-center touch-pan-y cursor-grab active:cursor-grabbing"
                    >
                        <div className="bg-gradient-to-b from-[#353232] to-[#232323] absolute -bottom-2 left-0 right-0 m-auto w-[170px] h-[170px] rounded-[4px] z-0" />
                        <div className="flex flex-col items-center z-10">
                            <span className="text-white text-[14px] font-semibold">
                                {toRoman(slideIndex + 1)}
                            </span>

                            {isCryptoOnly ? (
                                <div className="w-[100px] h-[100px] flex items-center justify-center">
                                    <div
                                        className="w-[72px] h-[72px] rounded-full border-[2px] flex items-center justify-center"
                                        style={{ borderColor: cryptoColor?.border }}
                                    >
                                        <span
                                            className="text-[28px] font-bold"
                                            style={{ color: cryptoColor?.text }}
                                        >
                                            {toRoman(slideIndex + 1)}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="relative w-[100px] h-[100px]">
                                    {!imageLoaded && !imageError && crystalImage && (
                                        <div className="w-[100px] h-[100px] bg-black/10 rounded animate-pulse absolute inset-0" />
                                    )}
                                    {crystalImage ? (
                                        <img
                                            width={100}
                                            height={100}
                                            src={crystalImage}
                                            className={`w-[100px] h-[100px] rounded object-contain transition-opacity duration-200 ${
                                                imageLoaded ? 'opacity-100' : 'opacity-0'
                                            }`}
                                            onLoad={() => setImageLoaded(true)}
                                            onError={() => setImageError(true)}
                                            alt=""
                                            draggable={false}
                                        />
                                    ) : (
                                        <div className="w-[100px] h-[100px] bg-black/10 rounded flex items-center justify-center">
                                            <span className="text-white text-[12px]">?</span>
                                        </div>
                                    )}
                                    {imageError && (
                                        <div className="w-[100px] h-[100px] bg-black/10 rounded flex items-center justify-center absolute inset-0">
                                            <span className="text-white text-[12px]">?</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            <span className="text-[16px] font-medium text-white mb-2">
                                {rewardTier.name} reward
                            </span>

                            {isCryptoOnly || (promo?.promoType === 'crystalsAndCrypto') ? (
                                <CoinBadge
                                    token={tokenLogo}
                                    icon={tokenLogo ? undefined : 'ETH'}
                                    count={formatRewardModalCoinBadgeCount(rewardAmount)}
                                    countOnly
                                    isCenter
                                    fixedWidth={150}
                                    customColorText="#DEC315"
                                    fullCount
                                />
                            ) : (
                                <CoinBadge
                                    token={crystalImage || undefined}
                                    count={rewardAmount}
                                    countOnly
                                    isCenter
                                    fixedWidth={150}
                                    customColorText="#DEC315"
                                    classicCount
                                />
                            )}

                            {!isOnlyCrystals && !isBonusPromo && (
                                <div className="flex items-center gap-1 my-2">
                                    <img
                                        width={24}
                                        height={24}
                                        src={tokenLogo || getIcon(blockchainKey)}
                                        alt={tokenSymbol}
                                        className={`flex-shrink-0 object-contain ${tokenLogo ? 'rounded-full' : ''}`}
                                        draggable={false}
                                    />
                                    <span className="text-white text-[12px] font-semibold truncate max-w-[120px]">
                                        {tokenSymbol}
                                    </span>
                                </div>
                            )}

                            <span className="text-white text-[12px]">
                                {Number(winPercent).toFixed(1)}% chance
                            </span>
                        </div>
                    </motion.div>
                </AnimatePresence>

                <AnimatePresence>
                    {showSwipeHint && positionsCount > 1 && (
                        <motion.div
                            className="flex justify-center mt-6 pointer-events-none"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Lottie
                                animationData={swipeAnim}
                                loop
                                style={{ width: 72, height: 72 }}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </motion.div>
    );
}

// Функция для конвертации числа в римские цифры
const toRoman = (num) => {
    if (num <= 0 || num > 3999) return num.toString();
    
    const romanNumerals = [
        { value: 1000, numeral: 'M' },
        { value: 900, numeral: 'CM' },
        { value: 500, numeral: 'D' },
        { value: 400, numeral: 'CD' },
        { value: 100, numeral: 'C' },
        { value: 90, numeral: 'XC' },
        { value: 50, numeral: 'L' },
        { value: 40, numeral: 'XL' },
        { value: 10, numeral: 'X' },
        { value: 9, numeral: 'IX' },
        { value: 5, numeral: 'V' },
        { value: 4, numeral: 'IV' },
        { value: 1, numeral: 'I' }
    ];
    
    let result = '';
    for (const { value, numeral } of romanNumerals) {
        while (num >= value) {
            result += numeral;
            num -= value;
        }
    }
    return result;
};

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';

const toSubscript = (value) =>
    String(value).split('').map((digit) => SUBSCRIPT_DIGITS[Number(digit)]).join('');

const formatLeadingZeroDecimal = (num) => {
    if (!(num > 0 && num < 1)) return null;

    const normalized = num.toFixed(20).replace(/(\.\d*?)0+$/, '$1');
    const match = normalized.match(/^0\.(0+)([1-9]\d*)$/);
    if (!match) return null;

    const zeroCount = match[1].length;
    if (zeroCount < 2) return null;

    const significant = match[2].slice(0, 2);
    return `0.0${toSubscript(zeroCount)}${significant}`;
};

export default function DetailInfoTable(){
    const [isExpanded, setIsExpanded] = useState(true);
    const [showCategoryDetails, setShowCategoryDetails] = useState(false);  // Детали категорий (%, winners)
    const contentRef = useRef(null);
    const promo = useSelector((state) => state.selectedPromo.promo);
    const [token,setToken] = useState();
    const [gems, setGems] = useState([]);
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });
    const [blockchainInfo, setBlockchainInfo] = useState(null);
    const [positionModalIndex, setPositionModalIndex] = useState(null);
    
    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };
    
    const toggleCategoryDetails = () => {
        setShowCategoryDetails(!showCategoryDetails);
    };
    console.log(promo?.promoters)

    // Определяем тип промо
    const isBonusPromo = promo?.isBonusPromo === true;
    const isOnlyCrystals = promo?.promoType === 'onlyCrystals';

    // Обратный отсчёт до завершения промо — только после реального старта (ACTIVE)
    useEffect(() => {
        const durationDays = promo?.durationDays || 7;
        const status = promo?.status;
        const isFinished = status === 'COMPLETED' || status === 'MANUALLY_COMPLETED';
        const isRunning = status === 'ACTIVE';

        if (isFinished) {
            setTimeLeft({ days: 0, hours: 0, minutes: 0 });
            return undefined;
        }

        // Пока промо не стартовало — показываем полную длительность, таймер не тикает
        if (!isRunning || !promo?.startDate || !promo?.durationDays) {
            setTimeLeft({
                days: durationDays,
                hours: 0,
                minutes: 0,
            });
            return undefined;
        }

        const calculateTimeLeft = () => {
            const startDate = new Date(promo.startDate);
            const endDate = new Date(startDate.getTime() + promo.durationDays * 24 * 60 * 60 * 1000);
            const now = new Date();
            const diff = endDate.getTime() - now.getTime();

            if (diff <= 0) {
                setTimeLeft({ days: 0, hours: 0, minutes: 0 });
                return;
            }

            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

            setTimeLeft({ days, hours, minutes });
        };

        calculateTimeLeft();
        const interval = setInterval(calculateTimeLeft, 60000);
        return () => clearInterval(interval);
    }, [promo?.startDate, promo?.durationDays, promo?.status]);

    // Запрос токена по адресу и гемов по позициям
    useEffect(() => {
        const fetchData = async () => {
            // Запрос токена (пропускаем если нет tokenType или это onlyCrystals)
            if (promo?.tokenType && promo?.tokenType.length > 0 && promo?.promoType !== 'onlyCrystals') {
                try {
                    const tokenData = await tokenApi.lookupToken(promo?.tokenType, 't')
                    console.log('📦 DetailInfoTable: Полный ответ токена:', tokenData);
                    setToken(tokenData)
                } catch (error) {
                    console.error('❌ DetailInfoTable: Ошибка при получении токена:', error);
                    console.error('❌ DetailInfoTable: Детали ошибки:', error?.response?.data || error?.message);
                    console.error('❌ DetailInfoTable: Статус ошибки:', error?.response?.status);
                }
            } else if (promo?.promoType === 'onlyCrystals') {
                console.log('⚠️ DetailInfoTable: onlyCrystals промо - токен не требуется');
            } else {
                console.log('⚠️ DetailInfoTable: tokenType отсутствует в promo');
            }

            // Запрос гемов по позициям
            if (promo?.category?.positions && Array.isArray(promo.category.positions) && promo.category.positions.length > 0) {
                try {
                    const positionsLength = promo.category.positions.length;
                    // Создаем массив от 1 до длины массива позиций
                    const positionsArray = Array.from({ length: positionsLength }, (_, i) => i + 1);
                    console.log('🔍 DetailInfoTable: Запрос гемов по позициям:', positionsArray);
                    console.log('🔍 DetailInfoTable: Длина массива позиций:', positionsLength);
                    const gemsData = await CrystalsApi.getGemsByPositions(positionsArray);
                    console.log('✅ DetailInfoTable: Данные гемов получены:', gemsData);
                    setGems(gemsData);
                } catch (error) {
                    console.error('❌ DetailInfoTable: Ошибка при получении гемов:', error);
                    console.error('❌ DetailInfoTable: Детали ошибки:', error?.response?.data || error?.message);
                }
            } else {
                console.log('⚠️ DetailInfoTable: category.positions отсутствует или пуст в promo');
            }
        };
        fetchData();
    }, [promo?.tokenType, promo?.category?.positions]);

    useEffect(() => {
        if (!promo?.chainId || !promo?.id) return;
        blockchainApi.getPromoBlockchainInfo(promo.id)
            .then(res => setBlockchainInfo(res.data))
            .catch(err => console.error('Failed to load blockchain info:', err));
    }, [promo?.chainId, promo?.id]);

    const TimeBox =({number,symbol})=><div className="flex flex-row items-center gap-[4px] ">

    <div className={'w-[25px] h-[25px]  bg-[#1A1A1A] rounded-[3px] flex flex-row items-center justify-center'}>
            <span className={`text-white text-[11px] font-semibold`}>{number}</span>
     </div>
    <span className={`text-white text-[11px]`}>{symbol}</span>

    </div>
    const PromouterAndTime = ()=><div className="w-full flex flex-row items-center justify-between px-[17.5px]">
        <div className={`flex-1 h-[50px] ml-2 flex flex-row items-center justify-start gap-[15px] min-w-0 overflow-hidden`}>
            <Avatar width={40} height={40} label={'octo'} icon={getPromoterAvatarSrc(promo?.promoters?.[0]?.link, promo?.promoters?.[0])}/>
            <div className="w-full h-[25px] flex items-center justify-start ">
                <span className={`text-white text-[13px] truncate flex-1 min-w-0`}>{getPromoterDisplayName(promo?.promoters?.[0])}</span>
            </div>
            
     
        </div>
        <div className={` h-[50px] flex flex-row items-center gap-[3px] flex-shrink-0 ml-2`}>
            <TimeBox number={timeLeft.days} symbol={'D'}/>
            {/* <TimeBox number={timeLeft.hours} symbol={'H'}/>
            <TimeBox number={timeLeft.minutes} symbol={'M'}/> */}
        </div>
    </div>


    const PromoRow =()=>{
        const [showPrizePoolTooltip, setShowPrizePoolTooltip] = useState(false);
        const prizePoolRef = useRef(null);

        useEffect(() => {
            if (!showPrizePoolTooltip) return;

            const handleClickOutside = (event) => {
                if (prizePoolRef.current && !prizePoolRef.current.contains(event.target)) {
                    setShowPrizePoolTooltip(false);
                }
            };

            document.addEventListener('pointerdown', handleClickOutside);
            return () => document.removeEventListener('pointerdown', handleClickOutside);
        }, [showPrizePoolTooltip]);

        // Для onlyCrystals или бонусных промо показываем кристалл и количество
        const showCrystalBadge = isOnlyCrystals || isBonusPromo;
        const crystalCount = promo?.category?.positions?.reduce((sum, p) => {
            const budget = Number(p?.budget);
            if (!Number.isNaN(budget) && budget > 0) {
                return sum + budget;
            }
            const winners = Number(p?.winners) || 0;
            const drop = Number(p?.dropPerWinner) || 0;
            return sum + (winners * drop);
        }, 0) || 0;
        // Берём последний гем из массива (самый массовый) для отображения в PromoRow
        const lastGem = gems.length > 0 ? gems[gems.length - 1] : null;
        const crystalImage = lastGem?.frontImage ? `https://api.duotetra.quest/uploads/gems/${lastGem.frontImage}` : null;

        const prizePoolAmount = showCrystalBadge
            ? crystalCount
            : (typeof promo?.totalAmount === 'number' ? promo.totalAmount.toFixed(2) : promo?.totalAmount);
        const prizePoolSymbol = showCrystalBadge
            ? (lastGem?.name || 'Crystals')
            : (token?.symbol || token?.name || 'ETH');
        
        return (
        <div className={`w-full h-[23px] flex flex-row items-center justify-between mt-[10px] px-[17.5px] `}>
            <div className={`w-fit px-1 flex flex-row items-center justify-center border-2 border-[#353232] rounded-[3.5px]`}>
                <span className={`text-white bg-none text-[11px] font-bold whitespace-nowrap`}>{promo?.category?.category_name}</span>
            </div>
            <div className={`w-[160px] relative`} ref={prizePoolRef}>
                <div
                    className="cursor-pointer"
                    onClick={() => setShowPrizePoolTooltip(v => !v)}
                >
                    {showCrystalBadge ? (
                        <CoinBadge 
                            count={crystalCount} 
                            token={crystalImage} 
                            iconHeight={20} 
                            iconWidth={20} 
                            isGiveaway={true}
                            isHeader={true}
                            headerToken={crystalImage || undefined}
                            selectedBlockchain={crystalImage ? undefined : 'diamond'}
                            fixedWidth={160}
                        />
                    ) : (
                        <CoinBadge
                            name={token?.name}
                            count={promo?.totalAmount}
                            token={'https://api.duotetra.quest/'+token?.logoPath}
                            iconHeight={20}
                            iconWidth={20}
                            isGiveaway={true}
                            isHeader={true}
                            selectedBlockchain={promo?.selectedBlockchain || 'ETH'}
                            fixedWidth={160}
                        />
                    )}
                </div>

                <AnimatePresence>
                    {showPrizePoolTooltip && (
                        <motion.div
                            initial={{ opacity: 0, y: -8, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -8, scale: 0.95 }}
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                            className='absolute top-[calc(100%+10px)] -left-7 min-w-[220px] min-h-20 bg-[#2373BE] z-10 rounded-[10px]'
                        >
                            <div className='w-full h-full flex flex-col items-center relative'>
                                <div className='w-[12px] h-[7px] bg-[#2373BE] [clip-path:polygon(50%_0%,0%_100%,100%_100%)] absolute -top-[6.5px] left-0 right-0 m-auto' />
                                <div className='w-full flex flex-col gap-1 p-3 text-center'>
                                    <span className='text-white text-[12px] font-semibold'>Giveaway Prize Pool</span>
                                    <span className='text-white/80 text-[12px]'>shared between all winners</span>
                                    <div className='w-full flex items-center justify-center'>
                                        <span className='text-white text-[11px]'>
                                            {prizePoolAmount}{' '}
                                            {prizePoolSymbol}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {showPrizePoolTooltip && (
                        <motion.div
                            initial={{ opacity: 0, y: -8, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -8, scale: 0.95 }}
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                            className='absolute top-[calc(100%+10px)] -left-7 min-w-[220px] min-h-20 bg-[#2373BE] z-10 rounded-[10px]'
                        >
                            <div className='w-full h-full flex flex-col items-center relative'>
                                <div className='w-[12px] h-[7px] bg-[#2373BE] [clip-path:polygon(50%_0%,0%_100%,100%_100%)] absolute -top-[6.5px] left-0 right-0 m-auto' />
                                <div className='w-full flex flex-col gap-1 p-3 text-center'>
                                    <span className='text-white text-[12px] font-semibold'>Giveaway Prize Pool</span>
                                    <span className='text-white/80 text-[12px]'>shared between all winners</span>
                                    <div className='w-full flex items-center justify-center'>
                                        <span className='text-white text-[11px]'>
                                            {prizePoolAmount}{' '}
                                            {prizePoolSymbol}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            <div className={`w-[60px] flex flex-row items-center justify-center border-2 border-[#353232] rounded-[3.5px]`}>
                <span className={`text-white bg-none text-[12px] font-bold`}>100%</span>
            </div>
        </div>
        );
    }

    // Цвета для cryptoOnly позиций
    const cryptoPositionColors = {
        3: getCrystalRarityColorMap(3),
        5: getCrystalRarityColorMap(5),
        7: getCrystalRarityColorMap(7),
    };

    // Форматирование числа: показываем 1 знак после точки, если есть дробная часть
    const formatMaxSixDigits = (value) => {
        if (value == null || value === '') return '';
        const raw = String(value);
        let digitCount = 0;
        let result = '';

        for (const ch of raw) {
            if (ch >= '0' && ch <= '9') {
                if (digitCount >= 6) break;
                digitCount += 1;
                result += ch;
            } else if (ch === '.' && !result.includes('.')) {
                result += ch;
            }
        }

        return result.replace(/\.$/, '');
    };

    const formatCompactCount = (value) => {
        if (value == null || value === '') return '';
        const num = Number(value);
        if (Number.isNaN(num)) return formatMaxSixDigits(value);

        if (num >= 1_000_000_000) {
            return `${(num / 1_000_000_000).toFixed(2).replace(/\.?0+$/, '')}B`;
        }
        if (num >= 1_000_000) {
            return `${(num / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
        }
        if (num >= 1000) {
            return `${(num / 1000).toFixed(2).replace(/\.?0+$/, '')}K`;
        }
        if (num > 0 && num < 1) {
            const subscriptFormatted = formatLeadingZeroDecimal(num);
            if (subscriptFormatted) return subscriptFormatted;
        }
        if (Number.isInteger(num)) {
            return String(num);
        }
        return formatMaxSixDigits(value);
    };

    // Обычный элемент категории (с кристаллом)
    const CategoriesItem = ({ pos, count, img, winners, winPercent, showDetails, onOpen }) => {
        const [showTooltip, setShowTooltip] = useState(false);
        const fullCount = Number(count);
        const hasDecimals = !Number.isInteger(fullCount) && fullCount.toString().split('.')[1]?.length > 1;
        
        const handleCountPress = (e) => {
            e.stopPropagation();
            if (hasDecimals) {
                setShowTooltip(true);
                setTimeout(() => setShowTooltip(false), 2000);
            }
        };

        const handleOpen = (e) => {
            e.stopPropagation();
            onOpen?.();
        };
        
        return (
            <div
                className=" flex flex-col items-center gap-[1px] flex-shrink-0 relative cursor-pointer"
                style={{ minWidth: '45px', maxWidth: '65px' }}
                onClick={handleOpen}
            >
                <span className={`text-[#959595] text-[9px]`}>{toRoman(pos)}</span>
                <span 
                    className={`text-[#DEC315] text-[12px] font-semibold ${hasDecimals ? 'cursor-pointer' : ''}`}
                    onClick={handleCountPress}
                >
                    {formatCompactCount(count)}
                </span>
                <AnimatePresence>
                    {showTooltip && (
                        <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            className="absolute top-[20px] bg-black/90 text-white text-[10px] px-2 py-1 rounded z-50 whitespace-nowrap"
                        >
                            {fullCount}
                        </motion.div>
                    )}
                </AnimatePresence>
                {showDetails && winPercent != null && (
                    <span className={`text-[#959595] text-[9px]`}>{winPercent.toFixed(1)}%</span>
                )}
                <img width={34} height={34} src={img} alt={`gem-${pos}`} style={{ width: '34px', height: '34px', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                {showDetails && winners > 0 && (
                    <span className={`text-white text-[11px] font-semibold`}>{winners}</span>
                )}
            </div>
        );
    }
    
    // Элемент категории для cryptoOnly (кружок с римской цифрой)
    const CryptoCategoriesItem = ({ pos, count, color, winners, winPercent, showDetails, onOpen }) => {
        const [showTooltip, setShowTooltip] = useState(false);
        const fullCount = Number(count);
        const shortCount = formatCompactCount(count);
        const hasDecimals = !Number.isInteger(fullCount) && fullCount.toString().split('.')[1]?.length > 1;
        
        const handleCountPress = (e) => {
            e.stopPropagation();
            if (hasDecimals) {
                setShowTooltip(true);
                setTimeout(() => setShowTooltip(false), 2000);
            }
        };

        const handleOpen = (e) => {
            e.stopPropagation();
            onOpen?.();
        };
        
        return (
            <div
                className="flex flex-col items-center gap-[1px] flex-shrink-0 relative cursor-pointer"
                style={{ minWidth: '45px', maxWidth: '65px' }}
                onClick={handleOpen}
            >
                <span className={`text-[#959595] text-[9px]`}>{toRoman(pos)}</span>
                <span 
                    className={`text-[#DEC315] text-[12px] font-semibold ${hasDecimals ? 'cursor-pointer' : ''}`}
                    onClick={handleCountPress}
                >
                    {shortCount}
                </span>
                <AnimatePresence>
                    {showTooltip && (
                        <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            className="absolute top-[20px] bg-black/90 text-white text-[10px] px-2 py-1 rounded z-50 whitespace-nowrap"
                        >
                            {fullCount}
                        </motion.div>
                    )}
                </AnimatePresence>
                {showDetails && winPercent != null && (
                    <span className={`text-[#959595] text-[9px]`}>{winPercent.toFixed(1)}%</span>
                )}
                <div 
                    className="w-[21px] h-[21px] rounded-full border-[1.5px] flex items-center justify-center"
                    style={{ borderColor: color.border }}
                >
                    <span 
                        className="text-[10px] font-bold"
                        style={{ color: color.text }}
                    >
                        {toRoman(pos)}
                    </span>
                </div>
                {showDetails && winners > 0 && (
                    <span className={`text-white text-[11px] font-semibold`}>{winners}</span>
                )}
            </div>
        );
    };

    const PromoCategories =()=>{
        // Позиции отображаются в обратном порядке: N … 1
        const positions = promo?.category?.positions || [];
        const promoType = promo?.promoType || 'crystalsAndCrypto';
        const isCryptoOnly = promoType === 'cryptoOnly';
        const positionsCount = positions.length;
        
        // Цвета для cryptoOnly
        const colors = cryptoPositionColors[positionsCount] || cryptoPositionColors[3];
        const gapClass = positionsCount === 5 ? 'gap-4' : positionsCount === 3 ? 'gap-7' : 'gap-2';
        
        // Создаем маппинг гемов по position для быстрого доступа
        const gemsMap = {};
        gems.forEach(gem => {
            if (gem.position) {
                gemsMap[gem.position] = gem;
            }
        });
        
        return (
            <div className={`w-full flex flex-col items-center mt-[13px]`}>
                <div 
                    className={`w-full flex flex-row items-center justify-center px-4 overflow-x-auto hide-scrollbar ${gapClass}`} 
                    style={{ flexWrap: 'nowrap' }}
                >
                {[...positions].reverse().map((position, reverseIndex) => {
                    const originalIndex = positions.length - 1 - reverseIndex;
                    const positionNumber = originalIndex + 1;
                    
                    if (isCryptoOnly) {
                        // Для cryptoOnly — кружки с римскими цифрами
                        const dropPerWinner = position?.dropPerWinner || position?.budget || 0;
                        const winners = position?.winners || 0;
                        const winPercent = position?.winPercent || 0;
                        const color = colors[originalIndex] || colors[colors.length - 1];
                        
                        return (
                            <div key={originalIndex} onClick={(e) => e.stopPropagation()}>
                                <CryptoCategoriesItem 
                                    pos={positionNumber} 
                                    count={dropPerWinner}
                                    winners={winners}
                                    winPercent={winPercent}
                                    color={color}
                                    showDetails={showCategoryDetails}
                                    onOpen={() => setPositionModalIndex(originalIndex)}
                                />
                            </div>
                        );
                    } else {
                        // Для остальных типов — кристаллы (C&C: crypto drop на иконке гема)
                        const gemPosition = positions.length - originalIndex;
                        const gem = gemsMap[gemPosition];
                        const winners = position?.winners || 0;
                        const winPercent = position?.winPercent || 0;
                        const count = promoType === 'crystalsAndCrypto'
                            ? resolvePositionDropPerWinner(position, promo)
                            : (position?.dropPerWinner || 0);
                        const frontImage = gem?.frontImage 
                            ? `https://api.duotetra.quest/uploads/gems/${gem.frontImage}`
                            : '';
                        
                        return (
                            <div key={originalIndex} onClick={(e) => e.stopPropagation()}>
                                <CategoriesItem 
                                    pos={positionNumber} 
                                    count={count}
                                    winners={winners}
                                    winPercent={winPercent}
                                    img={frontImage}
                                    showDetails={showCategoryDetails}
                                    onOpen={() => setPositionModalIndex(originalIndex)}
                                />
                            </div>
                        );
                    }
                })}
                </div>
            </div>
        );
    }  



    const WinnersRow =()=>{
        const maxParticipants = promo?.maxParticipantsCount || promo?.participantsCount || 0;
        const prizePlaces = promo?.prizePlacesCount || 0;
        const nonWinners = maxParticipants - prizePlaces;
        
        const winnersPercent = maxParticipants > 0 
            ? Math.round((prizePlaces / maxParticipants) * 100) 
            : 0;
        const nonWinnersPercent = maxParticipants > 0 
            ? Math.round((nonWinners / maxParticipants) * 100) 
            : 0;
        
        return (
            <div className={`w-full flex flex-row items-ceter justify-between px-[31px] mt-[9px]`}>
                <div className={`w-[70px] flex flex-col items-center`}>
                    <span className={`text-[10px] text-[#D8D8D8]`}>players</span>
                    <div className={`flex flex-row items-center justify-center gap-2`}>
                        <span className={`text-[12px] text-[#D8D8D8]`}>{maxParticipants}</span>
                        <img src={users} width={11} height={11}/>
                        <span className={`text-[9px] text-[#959595]`}>100%</span>
                    </div>
                </div>
                <div className={`w-[70px] flex flex-col items-center`}>
                    <span className={`text-[10px] text-[#D8D8D8]`}>winners</span>
                    <div className={`flex flex-row items-center justify-center gap-2`}>
                        <span className={`text-[12px] text-[#D8D8D8]`}>{prizePlaces}</span>
                        <img src={cup} width={12} height={12}/>
                        <span className={`text-[9px] text-[#959595]`}>{winnersPercent}%</span>
                    </div>
                </div>
                <div className={`w-[70px] flex flex-col items-center`}>
                    <span className={`text-[10px] text-[#D8D8D8]`}>non-winners</span>
                    <div className={`flex flex-row items-center justify-center gap-2`}>
                        <span className={`text-[12px] text-[#D8D8D8]`}>{nonWinners}</span>
                        <span className={`text-[9px] text-[#959595]`}>{nonWinnersPercent}%</span>
                    </div>
                </div>
    </div>
        );
    }
    
    const BlockchainRow = () => {
        if (!promo?.chainId) return null;
        const statusColors = {
            pending_fee: '#F59E0B',
            pending_deposit: '#F59E0B',
            funded: '#4ade80',
            claiming: '#60a5fa',
            completed: '#4ade80',
            withdrawn: '#959595',
        };
        const statusLabels = {
            pending_fee: 'Fee pending',
            pending_deposit: 'Deposit pending',
            funded: 'Funded',
            claiming: 'Claiming',
            completed: 'Completed',
            withdrawn: 'Withdrawn',
        };
        const status = blockchainInfo?.blockchainStatus || promo?.blockchainStatus;
        const color = statusColors[status] || '#959595';
        const label = statusLabels[status] || status || 'Pending';

        return (
            <div className='w-full flex flex-row items-center justify-between px-[31px] mt-[9px]'>
                <div className='flex flex-row items-center gap-[6px]'>
                    <div className='w-[8px] h-[8px] rounded-full' style={{ backgroundColor: color }} />
                    <span className='text-[10px] text-[#D8D8D8]'>Chain {promo.chainId}</span>
                </div>
                <div className='flex flex-row items-center gap-[4px]'>
                    <span className='text-[10px] font-semibold' style={{ color }}>{label}</span>
                </div>
                {blockchainInfo?.claims?.length > 0 && (
                    <span className='text-[10px] text-[#959595]'>
                        {blockchainInfo.claims.length} claim{blockchainInfo.claims.length !== 1 ? 's' : ''}
                    </span>
                )}
            </div>
        );
    };

    return(
        <div className="w-full border border-[#353232] rounded-[5px] flex flex-col pt-[13px] pb-[13px]">
                {/* mini title */}
                <div className={'w-full flex flex-row items-center justify-between  px-[17.5px]'}>
                        <div></div>

                        <div className={`text-white`}>
                            <span className="text-[14px] font-semibold">Promo By</span>
                        </div>

                        <motion.button
                            type="button"
                            className="cursor-pointer"
                            onClick={toggleCategoryDetails}
                            animate={{ rotate: showCategoryDetails ? 180 : 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                            <AccordionArrow />
                        </motion.button>
                </div>
                <PromouterAndTime/>
                <PromoRow/>

                <AnimatePresence>
                    {positionModalIndex != null && (
                        <PrizePoolModal
                            initialSlideIndex={positionModalIndex}
                            onClose={() => setPositionModalIndex(null)}
                            promo={promo}
                            gems={gems}
                            token={token}
                            isOnlyCrystals={isOnlyCrystals}
                            isBonusPromo={isBonusPromo}
                        />
                    )}
                </AnimatePresence>

                <AnimatePresence initial={false}>
                    {isExpanded && (
                        <motion.div
                            ref={contentRef}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                        >
                            <div className={`w-full h-[1px] bg-[#353232] mt-[13px]`}/>
                            <PromoCategories/>
                <div className={`w-full h-[1px] bg-[#353232] mt-[13px]`}/>
                            <WinnersRow/>
                            {promo?.chainId && (
                                <>
                                    <div className={`w-full h-[1px] bg-[#353232] mt-[9px]`}/>
                                    <BlockchainRow/>
                                </>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
        </div>
    )
}